import { firebaseAuth } from "../firebase";
import { Address } from "./models/company";

function responseOk(response: Response) {
  return response && response.status >= 200 && response.status < 300;
}

export type ApiRequestOptions = RequestInit & {
  jsonBody?: { [key: string]: any };
};

/**
 * A random id for this browser session (per tab, survives reloads), sent as
 * x-kono-session so the API's guest browse budgets can count per session
 * rather than only per IP. Absent outside the browser (tests, SSR).
 */
const browserSessionId = (): string | null => {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    let id = window.sessionStorage.getItem("kono-session");
    if (!id) {
      id =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Math.random().toString(36).slice(2)}-${Date.now()}`;
      window.sessionStorage.setItem("kono-session", id);
    }
    return id;
  } catch {
    return null;
  }
};

/**
 * Send an API request with the following processing added for convenience:
 *
 *  - Add commonly used headers to the request automatically
 *  - Stringify JSON
 *  - Throw an error if the response is not 2xx, so that you can avoid
 *    some error-handling code in your main promise result handler.
 */
export function sendRequest(
  url: string,
  requestOptions: ApiRequestOptions,
): Promise<Response> {
  // These headers apply to most requests so are added here for convenience.
  const sessionId = browserSessionId();
  const standardHeaders = {
    Accept: "application/json",
    "Content-Type": "application/json",
    ...(sessionId ? { "x-kono-session": sessionId } : {}),
  };

  requestOptions.headers = { ...standardHeaders, ...requestOptions.headers };

  if (requestOptions.jsonBody) {
    requestOptions.body = JSON.stringify(requestOptions.jsonBody);
  }

  return fetch(url, requestOptions).then(async (response) => {
    if (!responseOk(response)) {
      const errorText = await response.text();
      throw new Error(
        errorText
          ? `${response.status} ${errorText}`
          : `Request failed (${response.status})`,
      );
    }
    return response;
  });
}

/**
 * Send an API request that's authenticated with the current Firebase signed in user.
 *
 * Uses sendRequest() under the hood, so has the same opinions / convenience processing
 * as that function.
 */
export async function sendAuthenticatedRequest(
  url: string,
  requestOptions: ApiRequestOptions,
): Promise<Response> {
  const firebaseIdToken = await firebaseAuth.currentUser?.getIdToken();
  if (firebaseIdToken) {
    requestOptions.headers = {
      Authorization: `Bearer ${firebaseIdToken}`,
      ...requestOptions.headers,
    };
  }

  return sendRequest(url, requestOptions);
}

export async function sendVerificationEmailRequest(): Promise<Response> {
  const userApiBaseUrl = process.env.REACT_APP_USER_API_BASE_URL;
  if (!userApiBaseUrl) {
    throw new Error("Missing REACT_APP_USER_API_BASE_URL");
  }

  return sendAuthenticatedRequest(`${userApiBaseUrl}/emailVerification`, {
    method: "POST",
    jsonBody: {},
  });
}

/**
 * Asks our own backend to send the reset email, rather than calling Firebase
 * from the browser: Firebase's email is branded for the hosting project, ours is
 * branded for Kono and links back to our own page.
 */
export async function sendPasswordResetRequest(
  email: string,
): Promise<Response> {
  const userApiBaseUrl = process.env.REACT_APP_USER_API_BASE_URL;
  if (!userApiBaseUrl) {
    throw new Error("Missing REACT_APP_USER_API_BASE_URL");
  }
  // /users is authenticated; the reset route sits beside it on /auth.
  const authBase = userApiBaseUrl.replace(/\/users$/, "/auth");
  return sendRequest(`${authBase}/password-reset`, {
    method: "POST",
    jsonBody: { email },
  });
}

export function classNames(
  ...classes: (string | undefined | null | boolean)[]
) {
  return classes.filter(Boolean).join(" ");
}

const currencyFormatterCache = new Map<string, Intl.NumberFormat>();

/**
 * Format a monetary value with thousands separators and the correct currency
 * symbol, e.g. formatCurrency(14500, "EUR") => "€14,500.00".
 * Falls back to USD when no currency is provided.
 */
export function formatCurrency(
  value: number,
  currency?: string | null,
): string {
  const normalizedCurrency = (currency || "USD").toUpperCase();
  let formatter = currencyFormatterCache.get(normalizedCurrency);
  if (!formatter) {
    formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: normalizedCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    currencyFormatterCache.set(normalizedCurrency, formatter);
  }
  return formatter.format(value || 0);
}

/**
 * Format a plain number with thousands separators, e.g.
 * formatNumber(14500) => "14,500". Decimal places can be controlled via the
 * optional fractionDigits argument.
 */
export function formatNumber(
  value: number,
  fractionDigits = 0,
): string {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value || 0);
}

export function saveInputChangeInHookState(setStateFn: (v: any) => void) {
  return (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const target = event.target;
    const value =
      target.type === "checkbox"
        ? (target as HTMLInputElement).checked
        : target.value;
    setStateFn(value);
  };
}

export enum AddressFormat {
  Full,
  CityStateZip,
}

export function formatAddress(address: Address, format: AddressFormat) {
  if (!address) return "";

  switch (format) {
    case AddressFormat.Full:
      return `${address.streetLines.join(", ")}, ${address.city}, ${
        address.stateOrProvinceCode
      } ${address.postalCode}`;
    case AddressFormat.CityStateZip:
      return `${address.city}, ${address.stateOrProvinceCode} ${address.postalCode}`;
  }
}
